import { FieldArray, Form, Formik } from 'formik';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Col, Label, Row } from 'reactstrap';
import {
  addGLTemplate,
  editGLTemplate,
  getAccountNumber,
  getIndividualGLTemplate,
} from '../../../api/glTemplate';
import { Button, Input, Select, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import {
  TRANSACTION_TYPE_CREDIT,
  TRANSACTION_TYPE_DEBIT,
} from '../../../constants/gl-configuration';
import { apiErrorHandler, errorHandler, successHandler } from '../../../utils/handlers';
import AutoCompleteDropdown from '../common/AutoCompleteDropdown';
import AccountError from './AccountError';
import AccountRow from './AccountRow';
import AccountSetError from './AccountSetError';
import AccountSetsEmptyTile from './AccountSetsEmptyTile';
import AccountsItemEmptyTile from './AccountsItemEmptyTile';
import { GLTEMPLATE_ID_PREFIX, glTemplateValidationSchema, tableColumns } from './constants';

const GLTemplateForm = (props) => {
  const {
    onCancel,
    onCreateEditSuccess,
    glTypesData,
    glCategoriesData,
    glTransactionTypesData,
    editingTemplate,
  } = props;
  const [loading, setLoading] = useState(false);
  const [templateData, setTemplateData] = useState(null);

  // method for adding a new Account Set
  const handleAddACSet = (values, setFieldValue) => {
    const newSets = cloneDeep(values.accountSets);
    newSets.push([]);
    setFieldValue('accountSets', newSets);
  };

  // method for Adding a Account to an Account Set
  const handleAssignAccount = (values, setFieldValue) => {
    const accountSets = cloneDeep(values.accountSets);
    // -1 because array index starts with 0
    const setIndex = values.assignmentSet.value - 1;
    const newSets = [];

    accountSets.forEach((accountSet, index) => {
      if (index === setIndex) {
        const accountNumberExists = accountSet.find(
          (a) => a.accountNumber === values.assignmentAccountNumber.data.accountNumber,
        );
        if (accountNumberExists) {
          errorHandler(`Account number already exists in the set ${setIndex + 1}`);
        } else {
          accountSet.push({
            accountNumber: values.assignmentAccountNumber.data.accountNumber,
            description: values.assignmentAccountNumber.data.description,
            debit: false,
            credit: false,
            allocationPercentage: '',
            accountNumberData: {
              ...values.assignmentAccountNumber.data,
            },
          });
        }
      }
      newSets.push(accountSet);
    });
    setFieldValue('accountSets', newSets);
  };

  const getTransactionTypeId = (account) => {
    if (account.credit) {
      return glTransactionTypesData?.find((td) => td.name === TRANSACTION_TYPE_CREDIT)
        ?.TransactionTypeId;
    }

    if (account.debit) {
      return glTransactionTypesData?.find((td) => td.name === TRANSACTION_TYPE_DEBIT)
        ?.TransactionTypeId;
    }
    return null;
  };

  const formatAccountData = (account) => {
    return {
      accountNumber: account.accountNumber,
      description: account.description,
      debit:
        glTransactionTypesData?.find((td) => td.name === TRANSACTION_TYPE_DEBIT)
          ?.TransactionTypeId === account.transactionTypeId,
      credit:
        glTransactionTypesData?.find((td) => td.name === TRANSACTION_TYPE_CREDIT)
          ?.TransactionTypeId === account.transactionTypeId,
      allocationPercentage: account.percentage,
      accountNumberData: {
        accountNumber: account.accountNumber,
        accountNumberId: account.accountNumberId,
        description: account.description,
      },
    };
  };

  const createEditGLTemplate = async (values) => {
    const accountNumbers = [];
    if (values.accountSets.length > 0) {
      values.accountSets.forEach((accountSet, asIndex) => {
        if (accountSet?.length > 0) {
          accountSet.forEach((account) => {
            accountNumbers.push({
              setId: asIndex + 1,
              accountNumberId: account.accountNumberData.accountNumberId,
              percentage: account.allocationPercentage,
              transactionTypeId: getTransactionTypeId(account),
            });
          });
        }
      });
    }
    const payload = {
      identificationNumber: values.gltId,
      templateName: values.gltName,
      templateTypeId: values.gltType.value,
      templateCategoryId: values.gltCategory.value,
      description: values.gltDescription,
      templateAccountNumber: accountNumbers,
    };

    if (editingTemplate) payload.templateId = editingTemplate;
    setLoading(true);
    try {
      // edit
      if (editingTemplate) {
        await editGLTemplate(payload);
        successHandler({
          message: {
            message: 'GL Template Updated Successfully',
            title: 'Success',
          },
        });
      } else {
        // create
        await addGLTemplate(payload);
        successHandler({
          message: {
            message: 'GL Template Created Successfully',
            title: 'Success',
          },
        });
      }
      setLoading(false);
      onCreateEditSuccess();
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const getEditingTemplateData = async (id) => {
    setLoading(true);
    try {
      const response = await getIndividualGLTemplate(id);
      setTemplateData(response.data.data[0]);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  useEffect(() => {
    if (editingTemplate) getEditingTemplateData(editingTemplate);
  }, [editingTemplate]);

  const initValues = {
    accountSets: [],
    assignmentAccountNumber: null,
    assignmentSet: null,
    gltId: GLTEMPLATE_ID_PREFIX,
    gltName: '',
    gltType: null,
    gltCategory: null,
    gltDescription: '',
  };

  if (templateData) {
    initValues.gltId = templateData.identificationNumber;
    initValues.gltName = templateData.templateName;
    initValues.gltType =
      glTypesData?.length > 0 &&
      glTypesData.find((type) => type.value === templateData.templateTypeId);
    initValues.gltCategory =
      glCategoriesData?.length > 0 &&
      glCategoriesData.find((category) => category.value === templateData.templateCategoryId);
    initValues.gltDescription = templateData.description;

    if (templateData.TemplateAccountNumber?.length > 0) {
      // format the template sets data according to form data structure (see top of this file for the form's "set" Data structure)
      const tempSets = {};
      templateData.TemplateAccountNumber.forEach((an) => {
        if (an.setId in tempSets) {
          const setAccounts = [...tempSets[an.setId]];
          setAccounts.push(formatAccountData(an));
          tempSets[an.setId] = setAccounts;
        } else {
          tempSets[an.setId] = [formatAccountData(an)];
        }
      });
      const setKeys = Object.keys(tempSets);
      const accountSets = setKeys.map((k) => tempSets[k]);
      initValues.accountSets = accountSets;
    }
  }

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <Formik
      initialValues={initValues}
      enableReinitialize
      validationSchema={glTemplateValidationSchema}
      onSubmit={createEditGLTemplate}
    >
      {({ handleSubmit, values, setFieldValue, errors, touched }) => {
        // Dynamically generate options as new sets are added to the array
        const assignmentSetOptions = values.accountSets.map((_as, asi) => ({
          value: asi + 1,
          label: asi + 1,
        }));

        return (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg="6" className="atd-input account-number-input">
                <Label>GL Identification number</Label>
                <Input
                  name="gltId"
                  className="Account-input"
                  placeholder="GL - 1200"
                  formInput
                  value={values.gltId}
                  onChange={(e) => {
                    const val = e.target.value;
                    // do no allow user to remove the prefix "GL - "
                    if (!val?.startsWith(GLTEMPLATE_ID_PREFIX)) {
                      setFieldValue('gltId', GLTEMPLATE_ID_PREFIX);
                    } else {
                      setFieldValue('gltId', val);
                    }
                  }}
                />
              </Col>
              <Col lg="6" className="atd-input account-number-input">
                <Label>GL Template name</Label>
                <Input
                  name="gltName"
                  className="Account-input"
                  placeholder="GL Template Name"
                  formInput
                />
              </Col>
            </Row>
            <Row>
              <Col lg="6" className="atd-input account-number-input gl-tp-input">
                <Label>GL Template type</Label>
                <div className="common-local-search w-100">
                  <Select
                    classNamePrefix="select-dropdown"
                    size="lg"
                    name="gltType"
                    formInput
                    options={glTypesData}
                    onChange={(value) => setFieldValue('gltType', value)}
                    isClearable
                    value={values.gltType}
                  />
                </div>
              </Col>
              <Col lg="6" className="atd-input account-number-input gl-tp-input">
                <Label>GL template category</Label>
                <div className="common-local-search w-100">
                  <Select
                    classNamePrefix="select-dropdown"
                    size="lg"
                    name="gltCategory"
                    formInput
                    options={glCategoriesData}
                    onChange={(value) => setFieldValue('gltCategory', value)}
                    isClearable
                    value={values.gltCategory}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg="12" className="atd-input account-number-input">
                <Label>GL DESCRIPTION</Label>
                <Input
                  className="Account-input"
                  placeholder="Description"
                  name="gltDescription"
                  formInput
                />
              </Col>
            </Row>
            <Row>
              <Col lg="7" className="account-number-input">
                <Label>select account number</Label>
                <AutoCompleteDropdown
                  name="assignmentAccountNumber"
                  selection={values.assignmentAccountNumber}
                  setSelection={(value) => setFieldValue('assignmentAccountNumber', value)}
                  apiMethod={getAccountNumber}
                  responseFormatter={(d) => ({
                    value: d.accountNumberId,
                    label: d.accountNumber,
                    data: d,
                  })}
                  dropdownPlaceholder="Choose An account number"
                  loadDefault
                  loadOnClear
                />
              </Col>
              <Col lg="3" className=" account-number-input">
                <Label>select account set</Label>
                <div className="common-local-search w-100">
                  <Select
                    name="assignmentSet"
                    formInput
                    classNamePrefix="select-dropdown"
                    onChange={(value) => setFieldValue('assignmentSet', value)}
                    value={values.assignmentSet}
                    size="lg"
                    isClearable
                    options={assignmentSetOptions}
                  />
                </div>
              </Col>
              <Col lg="2" className="mt-5 gl-btn">
                <Button
                  color="primary"
                  className="atd-btn mt-1"
                  type="button"
                  onClick={() => handleAssignAccount(values, setFieldValue)}
                  disabled={
                    values.accountSets?.length === 0 ||
                    !values.assignmentAccountNumber ||
                    !values.assignmentSet
                  }
                >
                  Assign
                </Button>
              </Col>
            </Row>
            <Row className="mt-5">
              <Col lg="9">
                <h4 className="atd-heading">GL Template Details</h4>
              </Col>
              <Col>
                <Button
                  color="primary"
                  className="atd-btn mt-1"
                  type="button"
                  onClick={() => handleAddACSet(values, setFieldValue)}
                >
                  Add AC SET
                </Button>
              </Col>
            </Row>
            {/* Table logic */}
            <Row className="mt-3">
              <Col>
                <FieldArray
                  name="accountSets"
                  render={(setsArrayHelper) => {
                    if (values?.accountSets?.length > 0) {
                      return (
                        <div className="table-resp-scroll">
                          <Table
                            headers={tableColumns}
                            bordered
                            responsive
                            className="dashboard-table gl-table"
                          >
                            <tbody className="gl-table-body">
                              {values.accountSets?.map((accountSetData, accountSetIndex) => (
                                <Fragment key={`account-set-${accountSetIndex}`}>
                                  <FieldArray
                                    name={`accountSets.${accountSetIndex}`}
                                    render={(accountsArrayHelper) => {
                                      if (accountSetData?.length > 0) {
                                        return accountSetData.map((account, accountIndex) => (
                                          <Fragment key={`account-row-${account.accountNumber}`}>
                                            <AccountRow
                                              accountSetIndex={accountSetIndex}
                                              accountIndex={accountIndex}
                                              account={account}
                                              setFieldValue={setFieldValue}
                                              removeAccount={() =>
                                                accountsArrayHelper.remove(accountIndex)
                                              }
                                            />
                                            <AccountError
                                              touched={touched}
                                              errors={errors}
                                              accountSetIndex={accountSetIndex}
                                              accountIndex={accountIndex}
                                            />
                                          </Fragment>
                                        ));
                                      }
                                      return (
                                        <AccountsItemEmptyTile
                                          id={accountSetIndex + 1}
                                          removeSet={() => {
                                            setsArrayHelper.remove(accountSetIndex);
                                            setFieldValue('assignmentSet', null);
                                          }}
                                        />
                                      );
                                    }}
                                  />
                                  <AccountSetError
                                    touched={touched}
                                    errors={errors}
                                    accountSetIndex={accountSetIndex}
                                  />
                                </Fragment>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      );
                    }
                    return <AccountSetsEmptyTile touched={touched} errors={errors} />;
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="segment-btns">
                  <Button color="default" className="atd-btn" size="xs1" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button color="primary" className="atd-btn" size="xs1" type="submit">
                    Save
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

GLTemplateForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onCreateEditSuccess: PropTypes.func.isRequired,
  glTypesData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  glCategoriesData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  glTransactionTypesData: PropTypes.arrayOf(
    PropTypes.shape({
      TransactionTypeId: PropTypes.number,
      name: PropTypes.string,
    }),
  ).isRequired,
  editingTemplate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

GLTemplateForm.defaultProps = {
  editingTemplate: null,
};

export default GLTemplateForm;
